import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  ScatterChart, Scatter, XAxis, YAxis, ZAxis, CartesianGrid,
  ReferenceLine, Tooltip, ResponsiveContainer, Cell,
} from "recharts";
import { Compass } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { LockedPanel } from "@/components/entitlements/LockedPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PARTY_NAME = { D: "Democrat", R: "Republican", I: "Independent" };
const PARTY_DOT = { D: "#2F6BFF", R: "#FF3B3B", I: "#a855f7" };

const ideologyLabel = (d1) =>
  d1 <= -0.5 ? "Strongly Liberal"
  : d1 <= -0.2 ? "Liberal"
  : d1 < 0.2 ? "Moderate"
  : d1 < 0.5 ? "Conservative"
  : "Strongly Conservative";

const fmt = (v) => (v >= 0 ? `+${v.toFixed(3)}` : v.toFixed(3));

const NominateTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="rounded-lg border border-orange-500/35 px-3 py-2 text-xs shadow-2xl"
      style={{ background: "var(--whip-surface-2, #1A1A25)" }}>
      <p className="text-white font-semibold">{p.name}</p>
      <p className="text-white/50 font-mono">
        {p.party}-{p.state} · D1 {fmt(p.dim1)} · D2 {fmt(p.dim2)}
      </p>
    </div>
  );
};

export const NominateCard = ({ legislatorId, history }) => {
  const [showArc, setShowArc] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const arc = (history?.congresses || []).filter((c) => c.arcDim1 != null).map((c) => ({ dim1: c.arcDim1, dim2: c.arcDim2 ?? 0, congress: c.congress }));
  const { featureMode } = useAuth();
  const locked = featureMode("ideology_analysis") === "none";
  const [data, setData] = useState(null);
  const [group, setGroup] = useState("party");

  useEffect(() => {
    if (locked) return;
    axios
      .get(`${API}/legislators/${legislatorId}/nominate`, { timeout: 60000 })
      .then((r) => setData(r.data))
      .catch(() => setData({ error: true }));
  }, [legislatorId, locked]);

  const analysis = useMemo(() => {
    if (!data?.member) return null;
    const m = data.member;
    const peers = (data.peers || []).filter((p) => p.dim1 !== null && p.dim1 !== undefined);
    const otherParty = m.party === "D" ? "R" : "D";
    const groups = {
      party: {
        label: `${m.chamber} ${PARTY_NAME[m.party] || "Independent"}s`,
        rows: peers.filter((p) => p.chamber === m.chamber && p.party === m.party),
      },
      chamber: {
        label: `Full ${m.chamber}`,
        rows: peers.filter((p) => p.chamber === m.chamber),
      },
      other: {
        label: `${m.chamber} ${PARTY_NAME[otherParty]}s`,
        rows: peers.filter((p) => p.chamber === m.chamber && p.party === otherParty),
      },
      congress: {
        label: "All Congress (both chambers)",
        rows: peers,
      },
    };
    const g = groups[group];
    const rows = g.rows;
    const below = rows.filter((p) => p.dim1 < m.dim1 && p.bioguideID !== m.bioguideID).length;
    const above = rows.filter((p) => p.dim1 > m.dim1 && p.bioguideID !== m.bioguideID).length;
    const denom = rows.filter((p) => p.bioguideID !== m.bioguideID).length || 1;
    const liberalFraming = m.party === "D" || (m.party !== "R" && m.dim1 < 0);
    const percentile = Math.round(((liberalFraming ? above : below) / denom) * 100);
    const percentileWord = liberalFraming ? "liberal" : "conservative";
    const inGroup = rows.some((p) => p.bioguideID === m.bioguideID);
    let rankLine = null;
    if (inGroup) {
      const sorted = [...rows].sort((a, b) => a.dim1 - b.dim1);
      const idx = sorted.findIndex((p) => p.bioguideID === m.bioguideID);
      const fromLeft = idx + 1;
      const fromRight = sorted.length - idx;
      rankLine = fromLeft <= fromRight
        ? `#${fromLeft} most liberal of ${sorted.length}`
        : `#${fromRight} most conservative of ${sorted.length}`;
    }
    const sortedD1 = rows.map((p) => p.dim1).sort((a, b) => a - b);
    const median = sortedD1.length
      ? sortedD1[Math.floor(sortedD1.length / 2)]
      : 0;
    const groupIds = new Set(rows.map((p) => p.bioguideID));
    return { m, peers, groups, label: g.label, rows, percentile, percentileWord, rankLine, median, groupIds };
  }, [data, group]);

  if (data?.error || (data && !data.member)) {
    return (
      <div className="whip-surface rounded-xl border border-white/10 p-5" data-testid="nominate-card">
        <h2 className="text-[11px] font-mono font-bold uppercase tracking-[0.2em] text-white/60 flex items-center gap-2 mb-2">
          <Compass className="w-3.5 h-3.5 text-orange-400" /> Political Ideology
        </h2>
        <p className="text-white/50 text-sm">No ideology score published for this member yet.</p>
      </div>
    );
  }
  if (locked) {
    return (
      <LockedPanel
        feature="ideology_analysis"
        tier="pro"
        title="Political Ideology"
        blurb="Roll-call-derived ideology scoring with peer comparison across party, chamber, and all of Congress."
      />
    );
  }
  if (!analysis) return null;
  const { m, peers, label, percentile, percentileWord, rankLine, median, groupIds } = analysis;

  const GROUP_CHIPS = [
    { value: "party", label: "My Party" },
    { value: "chamber", label: "My Chamber" },
    { value: "other", label: "Other Party" },
    { value: "congress", label: "All Congress" },
  ];

  const dimmed = peers.filter((p) => !groupIds.has(p.bioguideID) && p.bioguideID !== m.bioguideID);
  const highlighted = peers.filter((p) => groupIds.has(p.bioguideID) && p.bioguideID !== m.bioguideID);
  const pct = (v) => `${Math.min(100, Math.max(0, ((v + 1) / 2) * 100))}%`;

  // Plain-English framing for non-technical readers.
  const partyRows = analysis.groups.party.rows.filter((p) => p.bioguideID !== m.bioguideID);
  const partyD1 = partyRows.map((p) => p.dim1).sort((a, b) => a - b);
  const q = (arr, f) => (arr.length ? arr[Math.min(arr.length - 1, Math.floor(arr.length * f))] : 0);
  const partyLo = q(partyD1, 0.1), partyHi = q(partyD1, 0.9);
  const partyName = PARTY_NAME[m.party] || "their party";
  const isR = m.party === "R";
  const partyPct = Math.round(((isR ? partyRows.filter((p) => p.dim1 < m.dim1).length : partyRows.filter((p) => p.dim1 > m.dim1).length) / (partyRows.length || 1)) * 100);
  const edgeWord = isR ? "conservative" : "liberal";
  const wing = partyPct >= 90 ? `the most ${edgeWord} wing`
    : partyPct >= 65 ? `the ${edgeWord} side`
    : partyPct > 35 ? "the center"
    : partyPct > 10 ? "the moderate side"
    : "the most moderate wing";
  const furtherWord = isR ? "further right" : "further left";
  const restPct = 100 - partyPct;
  const restPhrase = restPct >= 60 ? `roughly ${Math.round(restPct / 10)} in 10 vote ${furtherWord}`
    : restPct <= 10 ? `almost no one votes ${furtherWord}`
    : `about ${Math.round(restPct / 10)} in 10 vote ${furtherWord}`;
  const dim2Abs = Math.abs(m.dim2 ?? 0);
  const loyalty = dim2Abs < 0.25 ? "Usually votes the party line" : dim2Abs < 0.55 ? "Occasionally breaks with the party" : "Frequently breaks with the party";
  const loyaltyIdx = dim2Abs < 0.25 ? 0 : dim2Abs < 0.55 ? 1 : 2;
  const persuasion = loyaltyIdx === 0
    ? "Expect a party-line vote on partisan bills. Outreach pays off mainly on bipartisan or home-state issues."
    : loyaltyIdx === 1
      ? "Worth a targeted ask on cross-cutting issues (trade, immigration, regional interests) where this member has strayed before."
      : "A genuine swing vote on issues that split the parties internally. Prioritize direct outreach.";
  const cannotShow = !partyRows.length;

  return (
    <div className="whip-surface rounded-xl border border-white/10 overflow-hidden" data-testid="nominate-card">
      <div className="flex items-center justify-between px-5 py-3 border-b border-white/10 bg-white/[0.02]">
        <h2 className="text-[11px] font-mono font-bold uppercase tracking-[0.2em] text-white/60 flex items-center gap-2" title="Based on every recorded roll-call vote this Congress (Voteview DW-NOMINATE). Left is more liberal, right is more conservative.">
          <Compass className="w-3.5 h-3.5 text-orange-400" /> Political Ideology
        </h2>
        <span className="text-[11px] font-mono text-white/35">{data.congress}TH CONGRESS · VOTEVIEW</span>
      </div>
      <div className="p-5 space-y-5">
        {/* Takeaway */}
        <div className="rounded-lg border border-orange-500/30 bg-orange-500/[0.07] px-4 py-3" data-testid="nominate-takeaway">
          <p className="text-[10px] font-mono font-bold uppercase tracking-[0.18em] text-orange-300 mb-1">The takeaway</p>
          <p className="text-[15px] text-white leading-snug">
            <span className="font-bold">{m.name?.split(",")[0]}</span> sits in <span className="font-bold">{wing}</span> of {m.chamber} {partyName}s
            {cannotShow ? "." : <>: more {edgeWord} than <span className="font-black text-orange-300">{partyPct}%</span> of them, {restPhrase}.</>}
          </p>
          <p className="text-sm text-white/70 mt-1.5">{loyalty}. {persuasion}</p>
        </div>

        {/* Spectrum in words */}
        <div data-testid="nominate-spectrum">
          <p className="text-[10px] font-mono font-bold uppercase tracking-[0.18em] text-white/40 mb-2">Where they stand, compared with {label.toLowerCase().replace("my ", "the member's ")}</p>
          <div className="relative h-10">
            <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-3 rounded-full"
              style={{ background: "linear-gradient(90deg, #2F6BFF 0%, #6b7280 50%, #FF3B3B 100%)", opacity: 0.55 }} />
            {/* Party range band (middle 80%) */}
            <div className="absolute top-1/2 -translate-y-1/2 h-5 rounded-md border border-white/25 bg-white/10"
              style={{ left: pct(partyLo), width: `calc(${pct(partyHi)} - ${pct(partyLo)})` }}
              title={`Where most ${partyName}s fall`} data-testid="nominate-party-band" />
            <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-0.5 h-7 bg-white/80" style={{ left: pct(median) }} title={`Typical ${label}: middle of the group`} />
            <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-5 h-5 rounded-full border-[3px] border-orange-400 bg-[#0b0b0f] shadow-[0_0_12px_rgba(255,138,0,0.7)]"
              style={{ left: pct(m.dim1) }} title={`${m.name}`} data-testid="nominate-member-dot" />
          </div>
          <div className="grid grid-cols-5 text-[10px] font-mono uppercase tracking-wider text-white/45 mt-1">
            <span className="text-blue-300">Far left</span><span className="text-blue-200/80 text-center">Left of center</span><span className="text-center">Center</span><span className="text-red-200/80 text-center">Right of center</span><span className="text-red-300 text-right">Far right</span>
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-white/55 mt-2">
            <span className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded-full border-2 border-orange-400" /> {m.name?.split(",")[0]}</span>
            <span className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded-sm border border-white/30 bg-white/10" /> Where most {partyName}s fall</span>
            <span className="flex items-center gap-1.5"><span className="inline-block w-0.5 h-3 bg-white/80" /> Typical member of {label.toLowerCase()}</span>
          </div>
        </div>

        {/* Party loyalty gauge */}
        <div data-testid="nominate-loyalty">
          <p className="text-[10px] font-mono font-bold uppercase tracking-[0.18em] text-white/40 mb-2">Party loyalty on issues that split the parties internally</p>
          <div className="grid grid-cols-3 gap-1.5">
            {["Party line", "Sometimes breaks ranks", "Frequent swing vote"].map((t, i) => (
              <div key={t} className={`rounded-md px-2 py-2 text-center text-[11px] font-semibold border ${i === loyaltyIdx ? "border-orange-400/70 bg-orange-500/15 text-orange-200" : "border-white/10 text-white/35"}`}>{t}</div>
            ))}
          </div>
          <p className="text-[11px] text-white/45 mt-1.5">Read from how often this member votes against most of their party on trade, immigration, civil-liberties and regional votes.</p>
        </div>

        {/* Compare group chips */}
        <div className="flex flex-wrap items-center gap-2" data-testid="nominate-group-chips">
          <span className="text-[10px] font-mono font-bold uppercase tracking-[0.18em] text-white/35">Compare with</span>
          {GROUP_CHIPS.map((c) => (
            <button key={c.value} type="button" onClick={() => setGroup(c.value)}
              className={`text-xs px-3 py-1 rounded-full border transition-colors ${group === c.value ? "border-orange-500/60 bg-orange-500/15 text-orange-200 font-semibold" : "border-white/15 text-white/55 hover:text-orange-200 hover:border-orange-500/40"}`}
              data-testid={`nominate-group-${c.value}`}>{c.label}</button>
          ))}
          <span className="text-xs text-white/60 ml-1" data-testid="nominate-stats">
            More {percentileWord} than <span className="font-black text-orange-300">{percentile}%</span> of {label.toLowerCase()}{rankLine ? ` (${rankLine})` : ""}
          </span>
        </div>

        {/* Technical view */}
        <div>
          <button type="button" onClick={() => setShowMap((v) => !v)} data-testid="nominate-map-toggle"
            className="text-xs px-3 py-1 rounded-full border border-white/15 text-white/55 hover:text-orange-200 hover:border-orange-500/40 transition-colors">
            {showMap ? "Hide" : "Show"} the full ideology map (for analysts)
          </button>
          {showMap && (
            <div className="mt-3 space-y-2">
              <p className="text-[11px] text-white/45">Each dot is a member. Left-right is the main liberal-conservative axis (score {fmt(m.dim1)}). Up-down shows how often a member crosses party lines on social or regional issues (score {fmt(m.dim2)}). Dots near the middle line vote with their party.</p>
              {arc.length >= 2 && (
                <button type="button" onClick={() => setShowArc((v) => !v)} data-testid="nominate-career-arc-toggle"
                  className={`text-xs px-3 py-1 rounded-full border transition-colors ${showArc ? "border-orange-500/60 bg-orange-500/15 text-orange-200 font-semibold" : "border-white/15 text-white/55 hover:text-orange-200 hover:border-orange-500/40"}`}>
                  {showArc ? "Hide" : "Show"} career arc (how they moved over time)
                </button>
              )}
        {/* Ideology map */}
        <div className="h-72" data-testid="nominate-scatter">
          <ResponsiveContainer width="100%" height="100%">
            <ScatterChart margin={{ top: 8, right: 12, bottom: 18, left: 4 }}>
              <CartesianGrid stroke="rgba(255,255,255,0.05)" />
              <XAxis type="number" dataKey="dim1" domain={[-1, 1]} tick={{ fill: "rgba(255,255,255,0.35)", fontSize: 10 }}
                tickLine={false} axisLine={{ stroke: "rgba(255,255,255,0.1)" }}
                label={{
                  value: "More liberal          <-  Main voting axis  ->          More conservative",
                  position: "insideBottom", offset: -12,
                  fill: "rgba(255,255,255,0.55)", fontSize: 11,
                }} />
              <YAxis type="number" dataKey="dim2" domain={[-1, 1]} tick={{ fill: "rgba(255,255,255,0.35)", fontSize: 10 }}
                tickLine={false} axisLine={{ stroke: "rgba(255,255,255,0.1)" }}
                label={{
                  value: "Crosses party lines on social or regional issues",
                  angle: -90, position: "insideLeft", offset: 12,
                  style: { textAnchor: "middle" },
                  fill: "rgba(255,255,255,0.55)", fontSize: 11,
                }} />
              <ZAxis range={[28, 28]} />
              <ReferenceLine x={0} stroke="rgba(255,255,255,0.12)" />
              <ReferenceLine y={0} stroke="rgba(255,255,255,0.12)" />
              <Tooltip content={<NominateTooltip />} cursor={false} />
              <Scatter data={dimmed} fill="rgba(255,255,255,0.10)" isAnimationActive={false} />
              <Scatter data={highlighted} isAnimationActive={false}>
                {highlighted.map((p, i) => (
                  <Cell key={i} fill={PARTY_DOT[p.party] || PARTY_DOT.I} fillOpacity={0.55} />
                ))}
              </Scatter>
              <Scatter data={[m]} isAnimationActive={false} shape="circle">
                <Cell fill="#FF8A00" stroke="#fff" strokeWidth={1.5} />
              </Scatter>
              {showArc && arc.length >= 2 && (
                <Scatter data={arc} line={{ stroke: "#FF8A00", strokeWidth: 1.5, strokeDasharray: "4 3" }} lineType="joint"
                  isAnimationActive={false} shape={(props) => {
                    const { cx, cy, payload } = props;
                    const i = arc.indexOf(payload);
                    const isEnd = i === 0 || i === arc.length - 1;
                    return (
                      <g>
                        <circle cx={cx} cy={cy} r={isEnd ? 4 : 2.5} fill={isEnd ? "#FF8A00" : "rgba(255,138,0,0.7)"} stroke="#fff" strokeWidth={isEnd ? 1 : 0} />
                        {isEnd && (
                          <text x={cx} y={i === 0 ? cy - 8 : cy + 14} textAnchor="middle" fill="#ffd8b0" fontSize={10} fontWeight={700}>
                            {i === 0 ? `Start · ${payload.congress}th` : `Now · ${payload.congress}th`}
                          </text>
                        )}
                      </g>
                    );
                  }} />
              )}
            </ScatterChart>
          </ResponsiveContainer>
        </div>
        <p className="text-[10px] text-white/35 leading-relaxed mt-2">
          Scores are estimated from every roll-call vote a member has cast, on a scale from −1 (most liberal) to +1
          (most conservative). The secondary score captures cross-cutting social/regional divides.{" "}
          <span className="text-orange-300/80">Orange dot = this member</span>; colored dots = the selected comparison
          group. Source:{" "}
          <a href="https://voteview.com" target="_blank" rel="noreferrer" className="underline hover:text-orange-300">
            Voteview (UCLA)
          </a>.
        </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
